import {
	useQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import {
	getReviewClarifications,
	triageClarification,
	batchTriageClarifications,
	editClarificationText,
	applyToAll,
	linkDuplicates,
	completeReviewStep,
	requestAIReview,
} from "../api/clarification-review-api";
import type {
	ReviewClarification,
	TriageClarificationPayload,
	BatchTriagePayload,
	EditClarificationTextPayload,
	ApplyToAllPayload,
	DuplicateClarificationPayload,
	StepCompletePayload,
	ClarificationReviewSummary,
	AIReviewResponse,
} from "../types/clarification-review.type";
import { useMemo } from "react";

const reviewKeys = {
	all: (rfxId: number) => ["clarification-review", rfxId] as const,
	list: (rfxId: number) => [...reviewKeys.all(rfxId), "list"] as const,
};

export { reviewKeys };

export const useReviewClarifications = (
	rfxId: number | undefined
): UseQueryResult<ReviewClarification[]> => {
	return useQuery({
		queryKey: reviewKeys.list(rfxId!),
		queryFn: () => getReviewClarifications(rfxId!),
		enabled: !!rfxId,
	});
};

export const useReviewSummary = (
	clarifications: ReviewClarification[] | undefined
): ClarificationReviewSummary => {
	return useMemo(() => {
		const items = clarifications ?? [];
		return {
			total: items.length,
			reviewed: items.filter((c) => c.triage_status !== null).length,
			accepted: items.filter((c) => c.triage_status === "accepted").length,
			declined: items.filter((c) => c.triage_status === "declined").length,
			duplicate: items.filter((c) => c.triage_status === "duplicate").length,
			pending: items.filter((c) => c.triage_status === null).length,
		};
	}, [clarifications]);
};

export const useTriageClarification = (
	rfxId: number
): UseMutationResult<ReviewClarification | null, Error, { clarificationId: number; payload: TriageClarificationPayload }> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ clarificationId, payload }) =>
			triageClarification(rfxId, clarificationId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: reviewKeys.list(rfxId),
			});
		},
	});
};

export const useBatchTriage = (
	rfxId: number
): UseMutationResult<ReviewClarification[] | null, Error, BatchTriagePayload> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: BatchTriagePayload) =>
			batchTriageClarifications(rfxId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: reviewKeys.list(rfxId),
			});
		},
	});
};

export const useEditClarificationText = (
	rfxId: number
): UseMutationResult<ReviewClarification | null, Error, { clarificationId: number; payload: EditClarificationTextPayload }> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ clarificationId, payload }) =>
			editClarificationText(rfxId, clarificationId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: reviewKeys.list(rfxId),
			});
		},
	});
};

export const useApplyToAll = (
	rfxId: number
): UseMutationResult<ReviewClarification[] | null, Error, { clarificationId: number; payload: ApplyToAllPayload }> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ clarificationId, payload }) =>
			applyToAll(rfxId, clarificationId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: reviewKeys.list(rfxId),
			});
		},
	});
};

export const useLinkDuplicates = (
	rfxId: number
): UseMutationResult<ReviewClarification[] | null, Error, { clarificationId: number; payload: DuplicateClarificationPayload }> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ clarificationId, payload }) =>
			linkDuplicates(rfxId, clarificationId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: reviewKeys.list(rfxId),
			});
		},
	});
};

export const useCompleteReviewStep = (
	rfxId: number
): UseMutationResult<boolean, Error, StepCompletePayload> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: StepCompletePayload) =>
			completeReviewStep(rfxId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: reviewKeys.all(rfxId),
			});
		},
	});
};

export const useAIReview = (
	rfxId: number
): UseMutationResult<AIReviewResponse | null, Error, void> => {
	return useMutation({
		mutationFn: () => requestAIReview(rfxId),
	});
};
